import { useState } from "react";
import { getRoutePath, Link, useLocation } from "@/lib/simple-router";
import { Menu, X } from "lucide-react";
import { Button, buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export default function Navigation() {
  const [location] = useLocation();
  const pathname = getRoutePath(location);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navigationItems = [
    { href: "/villas", label: "Villas" },
    { href: "/amenities-experience", label: "Amenities" },
    { href: "/events-weddings", label: "Events & Weddings" },
    { href: "/guest-info", label: "Guest Info" },
    { href: "/blog", label: "Blog" },
    { href: "/contact", label: "Contact" },
  ];

  const isActive = (href: string) =>
    pathname === href || (href === '/blog' && pathname.startsWith('/blog/'));

  return (
    <nav className="bg-background shadow-sm fixed w-full top-0 z-50 border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 bg-background">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link
              href="/"
              onClick={(e) => {
                setMobileMenuOpen(false);
                if (pathname === "/") {
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}
            >
              <img
                src="/logo-white-tagline-optimized.webp"
                alt="Toka Ridge - Escape Together"
                className="h-16 w-auto cursor-pointer hover:opacity-90 transition-opacity"
                loading="eager"
                decoding="sync"
                width="213"
                height="64"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:block">
            <div className="ml-10 flex items-baseline space-x-6">
              {navigationItems.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  aria-current={isActive(item.href) ? 'page' : undefined}
                  className={`${isActive(item.href) ? 'text-toka-primary' : 'text-toka-text'} hover:text-[var(--toka-primary)] px-3 py-2 text-sm transition-colors cursor-pointer whitespace-nowrap`}
                  style={{ fontFamily: 'Lato', fontWeight: '400' }}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Book Now Button */}
          <div className="hidden lg:block shrink-0">
            <Link
              href="/villas"
              className={cn(buttonVariants(), "bg-toka-primary hover:bg-toka-primary/90 text-white px-6 py-3 rounded-lg")}
              style={{ fontFamily: 'Lato', fontWeight: '700' }}
            >
              Book Now
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <Button
              variant="ghost"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-[#ae6632] hover:text-[#2f3031] hover:bg-[#AD6433] p-2 h-16 w-16 flex items-center justify-center"
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            >
              {mobileMenuOpen ? (
                <X style={{ width: '27px', height: '27px' }} className="!w-7 !h-7" />
              ) : (
                <Menu style={{ width: '27px', height: '27px' }} className="!w-7 !h-7" />
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-background border-t">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {navigationItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                onClick={() => setMobileMenuOpen(false)}
                aria-current={isActive(item.href) ? 'page' : undefined}
                className={`block px-3 py-2 text-sm ${isActive(item.href) ? 'text-[var(--toka-primary)]' : 'text-toka-text'} hover:text-[var(--toka-primary)] focus-visible:text-[var(--toka-primary)] active:text-[var(--toka-primary)] transition-colors w-full text-left`}
                style={{ fontFamily: 'Lato', fontWeight: '400' }}
              >
                {item.label}
              </Link>
            ))}
            <Link
              href="/villas"
              onClick={() => setMobileMenuOpen(false)}
              className={cn(buttonVariants(), "w-full mt-4 bg-toka-primary hover:bg-toka-primary/90 text-white px-4 py-3 rounded-lg")}
              style={{ fontFamily: 'Lato', fontWeight: '700' }}
            >
              Book Now
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}
